<template>
  <div class="container mt-5">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/dashboard">Dashboard</router-link></li>
        <li class="is-active">
          <router-link to="/dashboard/report" aria-current="true">Reports</router-link>
        </li>
      </ul>
    </nav>
    <!-- The form submission explicitly passes false for adhoc reports -->
    <form @submit.prevent="getReport(false)" class="box">
      <!-- Row 1: Dates -->
      <div class="columns is-multiline is-mobile">
        <div class="column is-one-third">
          <div class="field">
            <label class="label is-small">Start Date:</label>
            <div class="control">
              <input class="input is-small" type="date" v-model="form.start_date" />
            </div>
          </div>
        </div>
        <div class="column is-one-third">
          <div class="field">
            <label class="label is-small">End Date:</label>
            <div class="control">
              <input class="input is-small" type="date" v-model="form.end_date" />
            </div>
          </div>
        </div>
      </div>

      <!-- Row 2: Tenant Account, Agent Code, Contract Number -->
      <div class="columns is-multiline is-mobile">
        <div class="column is-one-quarter">
          <div class="field">
            <label class="label is-small">Tenant Account:</label>
            <div class="control">
              <select class="input is-small" v-model="form.tenant_account" disabled>
                <option disabled value="">Select Tenant Account</option>
                <option v-for="entity in senderEntities" :key="entity.id" :value="entity">
                  {{ entity.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div class="column is-one-quarter">
          <div class="field">
            <label class="label is-small">Agent Code:</label>
            <div class="control">
              <input class="input is-small" type="text" v-model="form.agent_code" placeholder="All Agent Codes" />
            </div>
          </div>
        </div>
        <div class="column is-one-quarter">
          <div class="field">
            <label class="label is-small">Contract Number:</label>
            <div class="control">
              <input class="input is-small" type="text" v-model="form.contract_number" placeholder="All Contract Numbers" />
            </div>
          </div>
        </div>
      </div>

      <!-- Row 3: Buyer Name and Buyer Identity Type -->
      <div class="columns is-multiline is-mobile">
        <div class="column is-half">
          <div class="field">
            <label class="label is-small">Buyer Name:</label>
            <div class="control">
              <multiselect
                v-model="form.buyer"
                :options="buyers"
                label="name"
                track-by="id"
                :multiple="true"
                placeholder="All Buyers"
                :searchable="true"
                :close-on-select="true"
                :show-labels="true"
              ></multiselect>
            </div>
          </div>
        </div>
        <div class="column is-half">
          <div class="field">
            <label class="label is-small">Buyer Identity Type:</label>
            <div class="control">
              <multiselect
                v-model="form.buyer_identity_type"
                :options="identityOptions"
                :multiple="true"
                placeholder="All Identity Types"
                :close-on-select="false">
              ></multiselect>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 4: Sender Store Code, Invoice Type, Status -->
      <div class="columns is-multiline is-mobile">
        <div class="column is-one-third">
          <div class="field">
            <label class="label is-small">Department Code:</label>
            <div class="control">
              <multiselect
                v-model="form.sender_store_code"
                :options="storeCodes"
                :multiple="true"
                placeholder="All Departments"
                :close-on-select="false"
                track-by="id"
                label="code">
              ></multiselect>
            </div>
          </div>
        </div>
        <div class="column is-third">
          <div class="field">
            <label class="label is-small">Invoice Type:</label>
            <div class="control">
              <multiselect
                v-model="form.invoice_type"
                :options="uniqueInvoiceTypes"
                :multiple="true"
                placeholder="All Invoice Types"
                :close-on-select="false">
              ></multiselect>
            </div>
          </div>
        </div>
        <div class="column is-third">
          <div class="field">
            <label class="label is-small">Status:</label>
            <div class="control">
              <multiselect
                v-model="form.status"
                :options="statusOptions"
                :multiple="true"
                placeholder="All Statuses"
                :close-on-select="false">
              ></multiselect>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 5: Frequency Dropdown & Buttons -->
      <div class="columns is-multiline is-mobile">
        <div class="column is-one-quarter">
          <div class="field">
            <label class="label is-small">Report Frequency:</label>
            <div class="control">
              <select class="input is-small" v-model="form.frequency">
                <option disabled value="">Select Frequency</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
        </div>
        <!-- Two buttons: one for adhoc (Get Report) and one for scheduling -->
        <div class="column is-half">
          <div class="field is-grouped mt-5">
            <div class="control buttons">
              <!-- Get Report now explicitly calls getReport(false) -->
              <button class="button is-primary is-small" type="submit" :disabled="!isFilterApplied || isGeneratingReport">
                Get Report
              </button>
            </div>
            <div class="control">
              <button class="button is-info is-small" type="button" @click="scheduleReport" :disabled="!isScheduleValid">
                Schedule This Report
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>

    <hr />

    <!-- Scheduled Subscriptions Section (Not Paginated) -->
    <div class="section">
      <h2 class="title is-4">Scheduled Subscriptions</h2>
      <table class="table is-fullwidth is-striped is-small" v-if="scheduledReports.length">
        <thead>
          <tr>
            <th>Name</th>
            <th>Frequency</th>
            <th>Last Sent</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="report in scheduledReports" :key="report.id">
            <td>{{ report.name || getFileName(report.generated_file) }}</td>
            <td>{{ report.frequency }}</td>
            <td>{{ formatDate(report.last_sent) }}</td>
            <td>
              <button class="button is-danger is-small" @click="confirmDelete(report.id)">
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else>No scheduled subscriptions found.</p>
    </div>

    <!-- Past Requested Reports Section (Adhoc, Paginated) -->
    <div class="section">
      <h2 class="title is-4">Past Requested Reports</h2>
      <!-- Refresh button to re-fetch adhoc reports -->
      <button class="button is-info is-small" @click="fetchAdhocReports()">Refresh</button>
      <table class="table is-fullwidth is-striped is-small" v-if="adhocReports.length">
        <thead>
          <tr>
            <th>Name</th>
            <th>Report Type</th>
            <th>Created At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="report in adhocReports" :key="report.id">
            <td>{{ getReportName(report) }}</td>
            <td>{{ report.report_type }}</td>
            <td>{{ formatLastSent(report.last_sent) }}</td>
            <td class="buttons">
              <template v-if="report.generated_file">
                <a :href="report.generated_file" class="button is-small" target="_blank">
                  Download
                </a>
                <button class="button is-danger is-small" @click="confirmDelete(report.id)">
                  Delete
                </button>
              </template>
              <template v-else>
                Generating..
                <button class="button is-danger is-small" @click="confirmDelete(report.id)">
                  Delete
                </button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else>No past requested reports found.</p>
      <!-- Pagination Controls for Adhoc Reports -->
      <div class="pagination-controls buttons" style="margin-top: 10px;">
        <button class="button is-small" :disabled="!adhocPrevPage" @click="goToPrevPage">
          Previous
        </button>
        <button class="button is-small" :disabled="!adhocNextPage" @click="goToNextPage">
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import { mapState } from 'vuex';

export default {
  components: { Multiselect },
  data() {
    return {
      form: {
        start_date: '',
        end_date: '',
        submission_date: '',
        sender_entity_code: '',
        sender_store_code: [],
        document_type: '',
        invoice_type: '',
        gent_code: '',
        contract_number: '',
        created_by: '',
        status: '',
        buyer: null,
        tenant_account: null,
        frequency: '',
        buyer_identity_type: [],
      },
      reportUrl: '',
      userProfile: {},
      isGeneratingReport: false,
      buyers: [],
      statusOptions: [],
      identityOptions: [],
      uniqueInvoiceTypes: [],
      scheduledReports: [],
      adhocReports: [],
      adhocNextPage: null,
      adhocPrevPage: null,
    };
  },
  created() {
    this.fetchBuyers();
    this.getUniqueTypes();
    this.fetchScheduledReports();
    this.fetchAdhocReports();
  },
  computed: {
    ...mapState({
      storeCodes: state => state.user.store_code || [],
      senderEntities: state => state.user.sender_entities || [],
    }),
    isFilterApplied() {
      return (
        this.form.start_date ||
        this.form.end_date ||
        this.form.sender_entity_code ||
        this.form.sender_store_code ||
        this.form.invoice_type ||
        this.form.status ||
        this.form.buyer
      );
    },
    isScheduleValid() {
      return this.form.frequency !== '';
    }
  },
  watch: {
    senderEntities: {
      handler(newVal) {
        if (newVal.length === 1 && !this.form.tenant_account) {
          this.form.tenant_account = newVal[0];
        }
      },
      immediate: true,
    },
  },
  methods: {
    // getReport takes a boolean parameter (false for adhoc, true for scheduled)
    getReport(isScheduled = false) {
      const filters = {
        invoice_date__gte: this.form.start_date,
        invoice_date__lte: this.form.end_date,
        invoice_types: this.form.invoice_type,
        status: this.form.status,
        customer__name__icontains: this.form.buyer,
        sender_store_code: this.form.sender_store_code.map(store => store.code),
        agent_code: this.form.agent_code,
        contract_number: this.form.contract_number,
        customer__identity_type: this.form.buyer_identity_type,
      };

      const payload = {
        report_type: isScheduled ? 'scheduled' : 'adhoc',
        filters: filters,
      };

      if (isScheduled) {
        payload.frequency = this.form.frequency;
      }

      const endpoint = '/api/v1/notifications/reports/';
      axios.post(endpoint, payload)
        .then(response => {
          console.log(
            isScheduled
              ? 'Scheduled report initiated:'
              : 'Report generation initiated:',
            response.data
          );
          if (isScheduled) {
            this.fetchScheduledReports();
          } else {
            this.fetchAdhocReports();
          }
        })
        .catch(error => {
          console.error('Error generating report:', error);
        });
    },
    scheduleReport() {
      this.getReport(true);
    },
    async getUniqueTypes() {
      try {
        const response = await axios.get('/api/v1/unique-types/');
        this.uniqueInvoiceTypes = response.data.invoice_types || [];
        this.statusOptions = response.data.status_choices || [];
        this.identityOptions = response.data.identity_choices || [];
      } catch (error) {
        console.error('Error fetching unique types:', error);
      }
    },
    async fetchBuyers() {
      try {
        const response = await axios.get('/api/v1/customers/');
        this.buyers = response.data;
      } catch (error) {
        console.error('Error fetching buyers:', error);
      }
    },
    async fetchScheduledReports() {
      try {
        const response = await axios.get('/api/v1/notifications/reports/scheduled/');
        this.scheduledReports = response.data;
      } catch (error) {
        console.error('Error fetching scheduled reports:', error);
      }
    },
    async fetchAdhocReports(url = '/api/v1/notifications/reports/adhoc/') {
      try {
        const response = await axios.get(url);
        this.adhocReports = response.data.results;
        this.adhocNextPage = response.data.next;
        this.adhocPrevPage = response.data.previous;
      } catch (error) {
        console.error('Error fetching adhoc reports:', error);
      }
    },
    goToNextPage() {
      if (this.adhocNextPage) {
        this.fetchAdhocReports(this.adhocNextPage);
      }
    },
    goToPrevPage() {
      if (this.adhocPrevPage) {
        this.fetchAdhocReports(this.adhocPrevPage);
      }
    },
    async deleteReport(reportId) {
      try {
        // Call the custom delete endpoint
        await axios.delete(`/api/v1/notifications/reports/${reportId}/delete/`);
        this.fetchScheduledReports();
        this.fetchAdhocReports();
      } catch (error) {
        console.error('Error deleting report:', error);
      }
    },
    confirmDelete(reportId) {
      if (window.confirm("Are you sure you want to delete this report?")) {
        this.deleteReport(reportId);
      }
    },
    // Returns the report name; if not provided, extract it from the generated file URL.
    getReportName(report) {
      if (report.name) {
        return report.name;
      }
      if (report.generated_file) {
        const parts = report.generated_file.split('/');
        return parts[parts.length - 1];
      }
      return "No Name";
    },
    // Formats the last_sent field into a combined date and time string.
    formatLastSent(last_sent) {
      if (!last_sent) return "";
      const date = new Date(last_sent);
      const formattedDate = date.toISOString().split('T')[0].split('-').reverse().join('-');
      const formattedTime = date.toTimeString().split(' ')[0];
      return `${formattedDate} ${formattedTime}`;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0].split('-').reverse().join('-');
    },
    formatTime(dateString) {
      const date = new Date(dateString);
      return date.toTimeString().split(' ')[0].replace(/:/g, '');
    },
    // Helper method to extract file name from a URL.
    getFileName(url) {
      if (!url) return '';
      const parts = url.split('/');
      return parts[parts.length - 1];
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 800px;
  margin: 0 auto;
  padding-top: 20px;
}
.box {
  margin-bottom: 20px;
}
.notification {
  margin-top: 20px;
}
.title {
  margin-bottom: 10px;
}
.table {
  margin-top: 20px;
  font-size: 0.75rem;
}
.button {
  cursor: pointer;
}
.is-small {
  font-size: 0.75rem;
}
.pagination-controls {
  margin-top: 10px;
}
</style>
